import SystemPermission from "./SystemPermission";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {ISPAssigned, ISystemPermissionBase} from "@/types/models/ISystemPermission.ts";
import {IUser} from "@/types/models/IUser.ts";
import {IGetUser, IGetUsers} from "@/types/api/User.ts";
import {UserStatus} from "@/types/enums/UserStatus.ts";
import {UserType} from "@/types/enums/UserType.ts";
import {UserAffiliation} from "@/types/enums/UserAffiliation.tsx";
import {UserShirtSize} from "@/types/enums/UserShirtSize.tsx";
import {IWebAuthnCredential} from "@/types/models/IWebAuthnCredential.ts";

class User implements IUser {
    id: number;
    microsoft_oid: string | null;
    name: string;
    pronouns: string | null;
    status: UserStatus;
    type: UserType;
    username: string;
    pref_dark_mode: boolean;
    impersonated?: boolean;
    profile_photo: string | undefined;
    system_permissions_by_id: Record<number, ISPAssigned>;
    system_permissions_by_name: Record<string, ISPAssigned>;
    affiliation: UserAffiliation | null;
    phone_number_shared?: boolean;
    phone_number?: string;
    shirt_size_unisex?: UserShirtSize | null;
    shirt_size_womens?: UserShirtSize | null;
    prefer_womens_shirt?: boolean;
    pref_time_clock_enabled?: boolean;
    paychex_employee_id: string | null
    paychex_worker_id: string | null
    manager_id: number | null
    last_viewed_version_notes?: string
    security_keys?: IWebAuthnCredential[]

    constructor(data: IUser) {
        this.id = data.id;
        this.microsoft_oid = data.microsoft_oid;
        this.name = data.name;
        this.pronouns = data.pronouns;
        this.status = data.status;
        this.type = data.type;
        this.username = data.username;
        this.pref_dark_mode = data?.pref_dark_mode ?? false
        this.impersonated = data?.impersonated ?? false
        this.profile_photo = data["profile_photo"]
        this.affiliation = data["affiliation"]
        this.phone_number_shared = data["phone_number_shared"]
        this.phone_number = data["phone_number"]
        this.shirt_size_unisex = data["shirt_size_unisex"]
        this.shirt_size_womens = data["shirt_size_womens"]
        this.prefer_womens_shirt = data["prefer_womens_shirt"]
        this.paychex_worker_id = data["paychex_worker_id"]
        this.paychex_employee_id = data["paychex_employee_id"]
        this.pref_time_clock_enabled = data["pref_time_clock_enabled"]
        this.manager_id = data["manager_id"]
        this.system_permissions_by_id = {}
        this.system_permissions_by_name = {}
        this.last_viewed_version_notes = data["last_viewed_version_notes"]
        this.security_keys = data?.security_keys ?? []
        if (Object.prototype.hasOwnProperty.call(data, "system_permissions") && data.system_permissions) {
            data["system_permissions"].forEach((perm) => {
                let p = new SystemPermission(perm)
                this.system_permissions_by_name[p.module+":"+p.name] = p
                this.system_permissions_by_id[p.id] = p
            })
        }
    }

    static getAllUsers(showInactive=false, includePhotos=false): Promise<IUser[]> {
        let urlParams = new URLSearchParams()
        urlParams.append("all", showInactive ? "true" : "false")
        urlParams.append("photos", includePhotos ? "true" : "false")
        return OmniFetchTS<IGetUsers>("/api/v1/users/?"+urlParams.toString(), {method: "GET"}).then((json) => {
            if (json.success && json.data) {
                let users: IUser[] = []
                json.data.users.forEach((user) => {
                    users.push(new User(user))
                })
                return new Promise((resolve) => {
                    resolve(users)
                })
            } else {
                return new Promise((resolve) => {
                    resolve([])
                })
            }
        })
    }

    static getActiveUser(includePhoto=false): Promise<User | undefined> {
        let urlParams = new URLSearchParams()
        urlParams.append("photo", includePhoto ? "true" : "false")
        return OmniFetchTS<IGetUser>("/api/v1/users/me?"+urlParams.toString(), {method: "GET"}).then((json) => {
            if (json.success && json.data) {
                const user: IUser = json.data.user
                return new Promise((resolve) => {
                    resolve(new User(user))
                })
            } else {
                return new Promise((resolve) => {
                    resolve(undefined)
                })
            }
        })
    }

    static getUserById(id: string, includePhoto=false): Promise<User | undefined> {
        let urlParams = new URLSearchParams()
        urlParams.append("photo", includePhoto ? "true" : "false")
        return OmniFetchTS<IGetUser>(`/api/v1/users/${id}?`+urlParams.toString(), {method: "GET"}).then((json) => {
            if (json.success && json.data) {
                const user: IUser = json.data.user
                return new Promise((resolve) => {
                    resolve(new User(user))
                })
            } else {
                return new Promise((resolve) => {
                    resolve(undefined)
                })
            }
        })
    }

    hasSystemPermissionByName(module: string, name: string) {

        return Object.prototype.hasOwnProperty.call(this.system_permissions_by_name, module+":"+name) && this.system_permissions_by_name[module+":"+name]["user_has_perm"]
    }

    changeSystemPermissionById(id: number, value: boolean) {
        return OmniFetchTS("/api/v1/users/" + this.id + "/permission/" + id, {method: "POST"}, {value})
    }

    hasSystemPermission(sp: ISystemPermissionBase) {
        const spName = `${sp.module}:${sp.name}`
        return Object.prototype.hasOwnProperty.call(this.system_permissions_by_name, spName) && this.system_permissions_by_name[spName]["user_has_perm"]
    }
}

export default User